import { Injectable } from '@angular/core';
import { orderBy, sortBy, max, uniq, min, sumBy, findKey } from 'lodash-es';

import { LogService, ValueService } from '@oa/shared-core';

/**
 * Service for generic array functions, mainly a wrapper for lodash
 */
@Injectable()
export class ArrayService {

  private readonly _componentName = 'ArrayService';

  constructor(private log: LogService, private valueService: ValueService) {}

  /**
   * Orders items of a collection by a single property
   * @param items
   * @param propertyName
   */
  orderBy(items: any[], propertyName: string) {
    return orderBy(items, propertyName, 'asc');
  }

  /**
   * Orders items of a collection by a single property in descending order
   * @param items
   * @param propertyName
   */
  orderByDescending(items: any[], propertyName: string) {
    return orderBy(items, propertyName, 'desc');
  }

  public sortBy(items: any[], propertyName: string) {
    return sortBy(items, [propertyName]);
  }

  public sortByDescending(items: any[], propertyName: string) {
    const sorted = sortBy(items, [propertyName]);
    return sorted.reverse();
  }

  /**
   * Returns a unique set of items by a single property
   * @param items
   * @param propertyName
   */
  public getUniqueBy(items: any[], propertyName: string) {
    let uniqueItems = [];

    for (let index = 0; index < items.length; index++) {
      const element = items[index];

      if (!uniqueItems.some(x => x[propertyName] == element[propertyName])) {
        uniqueItems.push(element);
      }
    }

    return uniqueItems;
  }

  /**
   * Retrieves the maximum value of a collection.
   * @param items
   */
  max(items: any[]) {
    return max(items);
  }

  /**
   * Converts an array with duplicate values into an array
   * with no duplicates.
   * @param items
   */
  distinct(items: any[]) {
    return uniq(items);
  }

  /**
   * Retrieves the min value of a collection.
   * @param items
   */
  min(items: any[]) {
    return min(items);
  }

  sum(items: any[], propertyName: string): number {
    return sumBy(items, propertyName);
  }

  /**
   * counts the number of values in a CSV string
   * @param csv
   */
  countCSV(csv: string) {
    if (!csv) {
      return 0;
    }

    return csv.split(',').length;
  }

  /**
   * Gets the property value via the property name, ignoring case
   * @param obj
   * @param name
   */
  getValue(obj: any, name: string) {
    let realName = findKey(obj, function(value, key) {
      return key.toLowerCase() === name.toLowerCase();
    });

    return obj[realName];
  }

  /**
   * Checks to see if the value exists in the array
   * @param value
   * @param array
   */
  contains(value: any, array: any[]): boolean {
    if (!array) {
      return false;
    }

    if (!value) {
      return false;
    }

    // this method performs reasonable well across browsers.
    // https://stackoverflow.com/a/59627901/386619
    return array.indexOf(value) > -1;
  }

  /**
   * Convert to CSV
   * @param objArray
   */
  public convertToCSV(objArray: any) {
    let array = typeof objArray != 'object' ? JSON.parse(objArray) : objArray;

    let header = Object.keys(array[0]).join(',');

    let str = '';

    for (let i = 0; i < array.length; i++) {
      let line = '';
      // tslint:disable-next-line: forin
      for (let index in array[i]) {
        if (line != '') {
          line += ',';
        }
        if (array[i][index]) {
          line += `"${array[i][index]}"`;
        }
      }

      str += line + '\r\n';
    }

    return header + '\r\n' + str;
  }

  public toCharacterSeparatedValue(array: string[], delimiter: string) {
    let line = '';
    for (let i = 0; i < array.length; i++) {
      if (array[i]) {
        line = line + delimiter + array[i];
      }
    }

    return line;
  }

  /**
   * Splits a CSV string into an array
   * @param text
   * @param delimiter
   */
  public convertFromCSV(text: string, delimiter?: string) {
    if (!delimiter) {
      delimiter = ',';
    }

    if (!text) {
      return [];
    }

    const values = text.split(delimiter);

    return values;
  }

  /**
   * Checks to see if the CSV string contains a value
   * @param text
   * @param v
   */
  public csvContains(text: string, v: string) {
    const array = this.convertFromCSV(text);

    return array.some(s => this.valueService.equalsIgnoringCase(s, v));
  }

  /**
   * Finds a string value in an array strings, ignoring case. Returns the case-correct string from the matching array value.
   * @param values
   * @param x
   */
  public findIgnoreCase(values: string[], x: string) {
    if (!values || values.length === 0) {
      return null;
    }

    return values.find(k => this.valueService.equalsIgnoringCase(k, x));
  }

  /**
   * Returns the first item in an array
   * @param values
   */
  // tslint:disable-next-line: no-any
  public first(values: any[]) {
    if (values && values.length > 0) {
      return values[0];
    }

    return null;
  }
}
